import { useEffect } from 'react'
import type { ComponentProps } from 'react'
import useSWR from 'swr'
import LoadingSkeleton from '~/components/skeleton/sd/LoadingSkeleton'
import { groupByMealPeriodTab } from '~/lib/calendar/calendar'
import { useNow } from '~/lib/date/NowProvider'
import { addMonths, getEndOfMonth } from '~/lib/date/date'
import { dateTimeToDateText } from '~/lib/date/dateTime'
import { getFragmentData } from '~/lib/graphql'
import { fetcher } from '~/lib/graphql-request/swr'
import type { MealPeriod, ReservableCalendarItemFragment } from '~/lib/graphql/graphql'
import type { DateTime, HourMinute, RestaurantId } from '~/lib/graphql/scalars'
import type { MealPeriodTab } from '~/lib/mealPeriod/tab'
import { mealPeriodTabs } from '~/lib/mealPeriod/tab'
import type { GuideQuery } from '~/lib/query/guide'
import { GetReservableCalendar } from '../getReservableCalendar.graphql'
import { GetReservableCalendarAndMealPeriod } from '../getReservableCalendarAndMealPeriod.graphql'
import { ReservableCalendarFragment } from '../reservableCalendar.frag'
import MealPeriodsCalendarSectionClient from './MealPeriodsCalendarSectionClient'

type Props = {
  query: GuideQuery
  restaurantId: RestaurantId
  restaurant: ComponentProps<typeof MealPeriodsCalendarSectionClient>['restaurant']
  showIkyuDayBalloon: boolean
  onHasInventory: (hasInventory: boolean) => void
}

export function MealPeriodsCalendarSection({
  query,
  restaurantId,
  restaurant,
  showIkyuDayBalloon,
  onHasInventory,
}: Props) {
  const now = useNow()
  const { visitorsCount, visitTime } = query
  const { data, isLoading } = useSWR(
    // 二週間カレンダーの日付選択で開く、折り畳み状態の人数日時カレンダーは背が低く、裏で loading skeleton が見えるので常に key は指定
    // もともとは query key が JSON stringify に依存していたので loadCalendar で利用している値だけを key とする
    `${restaurantId}-${visitorsCount ?? ''}-${visitTime ?? ''}`,
    () => loadCalendar({ query, restaurantId, now }),
    { suspense: false, dedupingInterval: 10 * 60 * 1000 }
  )

  // 在庫があればページタイトルを書き替えたいため、親コンポーネントに通知する
  useEffect(() => {
    if (data) {
      onHasInventory(
        data.groupedCalendar.some((calendar) => calendar[1].dates.some((date) => date.reservable))
      )
    }
  }, [data, onHasInventory])

  if (isLoading || !data) {
    return <LoadingSkeleton height="200px" />
  }
  const { groupedCalendar } = data
  return (
    <MealPeriodsCalendarSectionClient
      groupedCalendar={groupedCalendar}
      restaurant={restaurant}
      restaurantId={restaurantId}
      showIkyuDayBalloon={showIkyuDayBalloon}
    />
  )
}

type Query = {
  restaurantId?: RestaurantId
  visitorsCount?: number
  visitTime?: HourMinute
  mealPeriod?: MealPeriod
}

// FIXME loader を使って取得する、直接 GraphQL を叩かない
async function loadCalendar({
  query,
  restaurantId,
  now,
}: {
  restaurantId: RestaurantId
  query: Query
  now: DateTime
}) {
  if (restaurantId === undefined) {
    return {
      groupedCalendar: [],
      defaultTab: undefined,
    }
  }
  const { visitorsCount, visitTime, mealPeriod } = query
  const today = dateTimeToDateText(now).dateText

  // 2週間カレンダーには14日分しか表示しないが、最短で予約可能な日付をリコメンド表示するために4カ月分取得する
  const end = getEndOfMonth(addMonths(today, 3)) // 3ヶ月後の月末日
  const input = {
    visitorsCount,
    from: today,
    to: end,
  }

  let reservableCalendarList: readonly ReservableCalendarItemFragment[]
  let defaultTab: MealPeriodTab | undefined = undefined
  if (mealPeriod && (mealPeriodTabs as unknown as MealPeriod[]).includes(mealPeriod)) {
    // mealPeriodTabs に含まれていることを確認しているので as でよい
    defaultTab = mealPeriod as MealPeriodTab
  }

  // 時間が指定されている場合は、その時間のMealPeriodタブをデフォルトで表示したいので
  // カレンダーに加えて予約可能なMealPeriodを取得する
  if (visitTime) {
    const data = await fetcher({
      query: GetReservableCalendarAndMealPeriod,
      variables: {
        restaurantId,
        input,
        searchInput: {
          visitorsCount,
          visitTime,
        },
      },
    })
    reservableCalendarList =
      getFragmentData(ReservableCalendarFragment, data.restaurant?.reservableCalendar) ?? []

    const reservableMealPeriods = data.restaurant?.searchCourses.nodes.flatMap((course) =>
      course.searchPlans.nodes.map((plan) => plan.mealPeriod)
    )
    // もともと defaultTab があればそれを優先して探索する
    defaultTab = [defaultTab, ...mealPeriodTabs].find((tab) =>
      reservableMealPeriods?.some((mealPeriod) => mealPeriod === tab)
    )
  } else {
    const data = await fetcher({
      query: GetReservableCalendar,
      variables: {
        restaurantId,
        input,
      },
    })
    reservableCalendarList =
      getFragmentData(ReservableCalendarFragment, data.restaurant?.reservableCalendar) ?? []
  }
  const groupedCalendar = groupByMealPeriodTab(reservableCalendarList)
  defaultTab = defaultTab ?? groupedCalendar[0]?.[0]
  return {
    groupedCalendar,
    defaultTab,
  }
}
